import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Row, Col, Input, DatePicker, notification, Card, Progress, Divider, Dropdown } from "antd";
import { EyeOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PartnerSelect from "../Partners/PartnerSelect";
import PartnerLink from "../Partners/PartnerLink";
import moment from "moment";
import RruleDaySelect from "./RruleDaySelect";
import { coreApi } from "../../api/calls";
import { formatDaysArray, getDays } from "../helpers/rrule";
import {getTheActiveDays, showActiveDays} from "../helpers/schedule";
import { validateSelectedUnits } from "../helpers/validation";

import OfferVacantHousesModal from "./OfferVacantHousesModal";
import OfferVacantHousesSentModal from "./OfferVacantHousesSentModal";
import { getCurrentUserEmployeeId } from "./managerNameOverride";
import ManagerSelect from "../ManagerSelect";

const PartnerInformSelect = ({ selectedUnits }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [substituteForm] = Form.useForm();
  const defaultManagerEmployeeId = getCurrentUserEmployeeId();

  const [isSubstituteModalVisible, setIsSubstituteModalVisible] = useState(false);
  const [isSubstituteButtonDisabled, setIsSubstituteButtonDisabled] = useState(true);
  const [substituteSummary, setSubstituteSummary] = useState([]);
  const [substituteEmailPreview, setSubstituteEmailPreview] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [globalNewPartner, setGlobalNewPartner] = useState(null);
  const [summary, setSummary] = useState([]);
  const [emailPreview, setEmailPreview] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isOfferVacantSentModalVisible, setIsOfferVacantSentModalVisible] = useState(false);
  const [isOfferVacantModalVisible, setIsOfferVacantModalVisible] = useState(false);

  const dropdownMenu = {
    items: [
      {
        key: "newHouseConfirmation",
        label: t("schedules.informPartnerOptions.newHouseConfirmation"),
        onClick: () => handleShowModal(),
      },
      {
        key: "substituteConfirmation",
        label: t("schedules.informPartnerOptions.substituteConfirmation"),
        onClick: () => handleShowSubstituteModal(),
      },
      {
        key: "offerVacantHousesForSubstitute",
        label: t("schedules.informPartnerOptions.offerVacantHousesForSubstitute"),
        onClick: () => handleShowOfferVacantHousesForSubstituteModal(),
      },
      {
        key: "offerVacantHouses",
        label: t("schedules.informPartnerOptions.offerVacantHouses"),
        onClick: () => handleShowOfferVacantHousesModal(),
      }
    ],
  };

  const getActiveDays = (record) => getTheActiveDays(record, t, true);

  useEffect(() => {
    form.resetFields();
    substituteForm.resetFields();
    form.setFieldValue("manager_employee_id", defaultManagerEmployeeId);
    substituteForm.setFieldValue("manager_employee_id", defaultManagerEmployeeId);
  }, [defaultManagerEmployeeId, form, selectedUnits, substituteForm]);

  const handleShowModal = () => {
    if (!selectedUnits?.length) return;

    const validationResult = validateSelectedUnits(selectedUnits, t);
    if (!validationResult.isValid) return;

    setGlobalNewPartner(selectedUnits[0]?.substitute_partner);
    setIsButtonDisabled(true);
    setSummary([]);
    setEmailPreview([]);
    setIsModalVisible(true);
  };

  const handleShowSubstituteModal = () => {
    if (!selectedUnits?.length) return;

    const validationResult = validateSelectedUnits(selectedUnits, t);
    if (!validationResult.isValid) return;

    setGlobalNewPartner(selectedUnits[0]?.substitute_partner);
    setIsSubstituteButtonDisabled(true);
    setSubstituteSummary([]);
    setSubstituteEmailPreview([]);
    setIsSubstituteModalVisible(true);
  };

  const handleShowOfferVacantHousesForSubstituteModal = () => {
    if (!selectedUnits?.length) return;

    setIsOfferVacantModalVisible(true);
  };

  const handleShowOfferVacantHousesModal = () => {
    if (!selectedUnits?.length) return;

    setIsOfferVacantSentModalVisible(true);
  };

  const handleApiError = (error) => {
    const isValidationError = !!error?.response?.data?.errors;

    notification.error({
      message: t(isValidationError ? "common.validationError" : "common.error"),
      description: error?.response?.data?.message || error.message,
    });
  };

  const buildPayload = (values) => {
    const building_units = selectedUnits.map((unit) => {
      const substitute_rrule = values.substitute_rrule?.[unit.id];
      const cleaningRange = values?.cleaning_range?.[unit.id] || [];
      const lastCleaning = cleaningRange[0];
      const firstCleaning = cleaningRange[1];
      return {
        id: unit.id,
        building_unit_id: unit.building_unit?.id || unit.building_unit_id,
        city_branch: unit.city_branch,
        address: `${unit.building_unit?.street || ""} ${unit.building_unit?.house_number || ""}`,
        last_cleaning_days: getActiveDays(unit),
        new_cleaning_days: formatDaysArray(substitute_rrule, t)?.join(", "),
        last_cleaning_date: lastCleaning ? moment(lastCleaning).format("YYYY-MM-DD") : null,
        first_cleaning_date: firstCleaning ? moment(firstCleaning).format("YYYY-MM-DD") : null,
      };
    });

    return {
      partner_id: selectedUnits?.[0]?.partner?.id,
      new_partner_id: globalNewPartner?.id,
      building_units,
      locale: selectedUnits?.[0]?.country_branch?.country_code_iso_3166?.toLowerCase() || "cs",
      manager_employee_id: values.manager_employee_id || null,
    };
  };

  const handleShowSummary = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const payload = buildPayload(values);

      const { data } = await coreApi.post("/partners/partner-change-email/preview", payload);
      setSummary(payload.building_units);
      setEmailPreview(data?.html || t("emails.mailError"));
      setIsButtonDisabled(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {

    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 99) {
          clearInterval(interval);
          return 99;
        }
        return p + 1;
      });
    }, 100);

    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const payload = buildPayload(values);

      await coreApi.post("/partners/partner-change-email/send", payload);
      notification.success({ message: t("emails.mailSent") });
      setIsModalVisible(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
      clearInterval(interval);
      setProgress(100);
    }
  };

  const handleShowSubstituteSummary = async () => {
    try {
      setIsSubmitting(true);
      const values = await substituteForm.validateFields();
      const payload = buildPayload(values);

      const { data } = await coreApi.post("/partners/substitute-confirm-email/preview", payload);
      setSubstituteSummary(payload.building_units);
      setSubstituteEmailPreview(data?.html || t("emails.mailError"));
      setIsSubstituteButtonDisabled(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubstituteSubmit = async () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 99) {
          clearInterval(interval);
          return 99;
        }
        return p + 1;
      });
    }, 100);

    try {
      setIsSubmitting(true);
      const values = await substituteForm.validateFields();
      const payload = buildPayload(values);

      await coreApi.post("/partners/substitute-confirm-email/send", payload);
      notification.success({ message: t("emails.mailSent") });
      setIsSubstituteModalVisible(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
      clearInterval(interval);
      setProgress(100);
    }
  };

  function applyBulkRange(dates) {
    if (!dates || dates.length !== 2) return;

    selectedUnits.forEach(unit => {
      substituteForm.setFieldValue(["cleaning_range", unit.id], dates);
    });
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <Dropdown menu={dropdownMenu} trigger={['click']}>
        <Button size="small">
          {t("schedules.informPartner")}
        </Button>
      </Dropdown>
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={1000}
        title={
          <>
          <strong>{t("schedules.informPartnerOptions.newHouseConfirmation")}. {" "}</strong>
            {t("partners.previewInformEmail")} {" - "} <strong>{t("schedules.partner")}:</strong> {" "}
            {selectedUnits?.[0]?.partner ? (
              <PartnerLink partner={selectedUnits[0].partner} />
            ) : (
              t("partners.noContractor")
            )}
          </>
        }
        footer={[
          <Button
            key="confirm"
            type="primary"
            icon={
              isSubmitting ? (
                <Progress
                  type="circle"
                  percent={progress}
                  width={24}
                  strokeWidth={10}
                  showInfo={true}
                  style={{ marginRight: 8 }}
                  status="active"
                />
              ) : (
                <SendOutlined />
              )
            }
            disabled={isButtonDisabled || isSubmitting}
            onClick={handleSubmit}
          >
            {t("common.confirm")}
          </Button>
        ]}
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={6}>
              <PartnerSelect
                key={selectedUnits?.[0]?.substitute_partner_id}
                name="new_partner_id"
                label={t("partners.newPartner")}
                required={true}
                validationMessage={t("cleanings.validation.partners")}
                onChange={setGlobalNewPartner}
                initialValue={selectedUnits?.[0]?.substitute_partner_id}
                disabled={true}
              />
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("emails.managerNameOverride")}
                name="manager_employee_id"
              >
                <ManagerSelect
                  withFormItem={false}
                  clearable
                  showSearch
                  activeOnly
                  placeholder={t("emails.managerNameOverride")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t("schedules.bulkTimeRangeSetting")}>
                <DatePicker.RangePicker
                  onChange={(dates) => {
                    if (!dates || dates.length !== 2) return;
                    selectedUnits.forEach(unit => {
                      form.setFieldValue(["cleaning_range", unit.id], dates);
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Divider />
          </Row>

          <Row gutter={12} style={{ fontWeight: "bold" }}>
            <Col span={6}>{t("schedules.addressEntrance")}</Col>
            <Col span={5}>{t("schedules.originalCleaningDay")}</Col>
            <Col span={4}>{t("schedules.newCleaningDay")}</Col>
            <Col span={9}>{t("schedules.newPartnerStartsCleaningFrom")}</Col>
          </Row>

          {selectedUnits && selectedUnits.map((unit) => {

            return (
              <Row gutter={12} key={unit.id}>
                <Col span={6}>
                  <Input disabled value={`${unit.building_unit?.street || ""} ${unit.building_unit?.house_number || ""}`} />
                </Col>
                <Col span={5}>
                  <Input
                    disabled
                    value={
                      showActiveDays(unit.building_unit.cleaning_days, unit.building_unit, t, {
                        partnerId: unit.partner?.id ?? unit.partner_id,
                        record: unit,
                      }) || "-"
                    }
                  />
                </Col>
                <Col span={4}>
                  <Form.Item
                    name={["substitute_rrule", unit.id]}
                  >
                    <RruleDaySelect
                      key={unit.id}
                      name={["substitute_rrule", unit.id]}
                      size="small"
                      className="mb-0"
                      rrule={unit?.substitute_rrule || unit?.rrule || null}
                      onChange={rruleString => {
                        const days = getDays(rruleString);
                        form.setFieldValue(["substitute_rrule", unit.id], days);
                      }}
                      postBlur={rruleString => {
                        const days = getDays(rruleString);
                        form.setFieldValue(["substitute_rrule", unit.id], days);
                      }}
                      showLabel={false}
                      isRequired={true}
                      disabled={true}
                    />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item
                    name={["cleaning_range", unit.id]}
                    initialValue={[moment(unit.action_week_from), moment(unit.action_week_to)]}
                    rules={[{ required: true, message: t("schedules.validation.cleaningRange") }]}
                  >
                    <DatePicker.RangePicker />
                  </Form.Item>
                </Col>
              </Row>
            );
          })}

          <Row justify="end" >
            <Col>
              <Button type="primary" onClick={handleShowSummary} icon={<EyeOutlined />} disabled={isSubmitting}>
                {t("common.continue")}
              </Button>
            </Col>
          </Row>

          {summary.length > 0 && (
            <Card style={{ marginTop: 24 }}>
              <iframe
                title="Email Preview"
                style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
                srcDoc={emailPreview}
              />
            </Card>
          )}
        </Form>
      </Modal>

      {/* Substitute Confirmation Modal */}
      <Modal
        open={isSubstituteModalVisible}
        onCancel={() => setIsSubstituteModalVisible(false)}
        width={1000}
        title={
          <>
            <strong>{t("schedules.informPartnerOptions.substituteConfirmation")}. {" "}</strong>
            {t("partners.previewInformEmail")} {" - "} <strong>{t("schedules.partner")}:</strong> {" "}
            {selectedUnits?.[0]?.partner ? (
              <PartnerLink partner={selectedUnits[0].partner} />
            ) : (
              t("partners.noContractor")
            )}
          </>
        }
        footer={[
          <Button
            key="confirm"
            type="primary"
            icon={
              isSubmitting ? (
                <Progress
                  type="circle"
                  percent={progress}
                  width={24}
                  strokeWidth={10}
                  showInfo={true}
                  style={{ marginRight: 8 }}
                  status="active"
                />
              ) : (
                <SendOutlined />
              )
            }
            disabled={isSubstituteButtonDisabled || isSubmitting}
            onClick={handleSubstituteSubmit}
          >
            {t("common.confirm")}
          </Button>
        ]}
      >
        <Form form={substituteForm}  layout="vertical"  initialValues={{
          cleaning_range: Object.fromEntries(
            selectedUnits.map(unit => [
              unit.id,
              [moment(unit.action_week_from), moment(unit.action_week_to)]
            ])
          )
        }}
        >
          <Row gutter={12}>
            <Col span={6}>
              <PartnerSelect
                key={selectedUnits?.[0]?.substitute_partner_id}
                name="new_partner_id"
                label={t("partners.substitutePartner")}
                required={true}
                validationMessage={t("cleanings.validation.partners")}
                onChange={setGlobalNewPartner}
                initialValue={selectedUnits?.[0]?.substitute_partner_id}
                disabled={true}
              />
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("emails.managerNameOverride")}
                name="manager_employee_id"
              >
                <ManagerSelect
                  withFormItem={false}
                  clearable
                  showSearch
                  activeOnly
                  placeholder={t("emails.managerNameOverride")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t("schedules.bulkTimeRangeSetting")}>
                <DatePicker.RangePicker
                  onChange={(dates) => {
                    applyBulkRange(dates);
                  }}
                />
              </Form.Item>
            </Col>
            <Divider />
          </Row>

          <Row gutter={12} style={{ fontWeight: "bold" }}>
            <Col span={6}>{t("schedules.addressEntrance")}</Col>
            <Col span={5}>{t("schedules.standardCleaningDay")}</Col>
            <Col span={4}>{t("schedules.substituteCleaningDay")}</Col>
            <Col span={9}>{t("schedules.substitutePartnerCleaningFromTo")}</Col>
          </Row>

          {selectedUnits && selectedUnits.map((unit) => {

            return (
              <Row gutter={12} key={unit.id}>
                <Col span={6}>
                  <Input disabled value={`${unit.building_unit?.street || ""} ${unit.building_unit?.house_number || ""}`} />
                </Col>
                <Col span={5}>
                  <Input
                    disabled
                    value={
                      showActiveDays(unit.building_unit.cleaning_days, unit.building_unit, t, {
                        partnerId: unit.partner?.id ?? unit.partner_id,
                        record: unit,
                      }) || "-"
                    }
                  />
                </Col>
                <Col span={4}>
                  <Form.Item
                    name={["substitute_rrule", unit.id]}
                  >
                    <RruleDaySelect
                      key={unit.id}
                      name={["substitute_rrule", unit.id]}
                      size="small"
                      className="mb-0"
                      rrule={unit?.substitute_rrule || unit?.rrule || null}
                      onChange={rruleString => {
                        const days = getDays(rruleString);
                        form.setFieldValue(["substitute_rrule", unit.id], days);
                      }}
                      postBlur={rruleString => {
                        const days = getDays(rruleString);
                        form.setFieldValue(["substitute_rrule", unit.id], days);
                      }}
                      showLabel={false}
                      isRequired={true}
                    />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item
                    name={["cleaning_range", unit.id]}
                    rules={[{ required: true, message: t("schedules.validation.cleaningRange") }]}
                  >
                    <DatePicker.RangePicker/>
                  </Form.Item>
                </Col>
              </Row>
            );
          })}

          <Row justify="end" >
            <Col>
              <Button type="primary" onClick={handleShowSubstituteSummary} icon={<EyeOutlined />} disabled={isSubmitting}>
                {t("common.continue")}
              </Button>
            </Col>
          </Row>

          {substituteSummary.length > 0 && (
            <Card style={{ marginTop: 24 }}>
              <iframe
                title="Email Preview"
                style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
                srcDoc={substituteEmailPreview}
              />
            </Card>
          )}
        </Form>
      </Modal>

      {/* Offer Vacant Houses for Substitute Modal */}
      {isOfferVacantModalVisible && <OfferVacantHousesModal
        isVisible={isOfferVacantModalVisible}
        onCancel={() => setIsOfferVacantModalVisible(false)}
        selectedUnits={selectedUnits}
        globalNewPartner={globalNewPartner}
        setGlobalNewPartner={setGlobalNewPartner}
        getTheActiveDays={getTheActiveDays}
        handleApiError={handleApiError}
      />}

      {/* Offer Vacant Houses Sending Modal */}
      {isOfferVacantSentModalVisible && <OfferVacantHousesSentModal
        isVisible={isOfferVacantSentModalVisible}
        onCancel={() => setIsOfferVacantSentModalVisible(false)}
        selectedUnits={selectedUnits}
        globalNewPartner={globalNewPartner}
        setGlobalNewPartner={setGlobalNewPartner}
        handleApiError={handleApiError}
      />}
    </div>
  );
};

export default PartnerInformSelect;
